/**
 * ag-variant-availability-fallback.js -- Astute Gear custom addition (2026-07-20)
 *
 * Fixes: on sparse variant matrices (e.g. Explorer 2000 Plus, where "+Battery Pack x2" only
 * pairs with "+200Wx2" solar), the stock Prestige <variant-picker> lets a shopper pick a value
 * on one option axis while another axis is left on a now-invalid value, showing "Unavailable"
 * instead of auto-correcting.
 *
 * HOW IT WORKS
 * First version of this script listened for the stock picker's own `variant:change` event, but
 * that event fires (synchronously, right after `product:rerender`) BEFORE the async DOM swap
 * that <product-rerender> performs actually completes -- so checking availability at that moment
 * read stale markup and never found anything to fix. Verified via manual testing on 2026-07-20.
 *
 * This version instead uses a MutationObserver on #main (which persists across re-renders, unlike
 * the variant-picker block itself, which gets replaced wholesale each time). Whenever the DOM
 * actually changes, it re-checks every <variant-picker> on the page: for each option axis other
 * than the one the shopper just clicked (tracked via a capture-phase "change" listener), if the
 * currently-checked value is now marked unavailable (`is-disabled` class on its label -- stock
 * option-value.liquid markup), it re-checks the first available value on that axis and dispatches
 * one native "change" event so the stock picker's own logic re-fetches/re-renders normally.
 *
 * Does not modify assets/theme.js or any other stock file -- safe across theme updates. Loaded
 * via one added <script> tag in layout/theme.liquid.
 *
 * Limitation: single-axis-per-pass correction; a 3+ axis product with pathological sparsity could
 * need more than one re-render pass to settle (each pass is still triggered correctly, just not
 * simultaneous). Fails safe if Prestige ever renames `is-disabled` -- shoppers just see the old
 * "Unavailable" behavior again, nothing breaks.
 */
(function () {
  "use strict";

  var lastChangedOptionPositionByForm = {};

  document.addEventListener(
    "change",
    function (event) {
      var target = event.target;
      if (target && target.matches && target.matches("input[data-option-position]")) {
        var formAttr = target.getAttribute("form");
        if (formAttr) {
          lastChangedOptionPositionByForm[formAttr] = target.getAttribute("data-option-position");
        }
      }
    },
    true
  );

  function isUnavailable(input) {
    if (!input) return false;
    var label = document.querySelector('label[for="' + input.id + '"]');
    return !!(label && label.classList.contains("is-disabled"));
  }

  function tryFixPicker(picker) {
    var formId = picker.getAttribute("form-id");
    var changedPos = lastChangedOptionPositionByForm[formId];
    var fieldsets = picker.querySelectorAll(".variant-picker__option");
    var fixedInput = null;

    fieldsets.forEach(function (fieldset) {
      if (fixedInput) return;
      var inputs = Array.prototype.slice.call(fieldset.querySelectorAll("input[data-option-position]"));
      if (inputs.length === 0) return;

      var optionPosition = inputs[0].getAttribute("data-option-position");
      if (optionPosition === changedPos) return;

      var checkedInput = fieldset.querySelector("input[data-option-position]:checked");
      if (!checkedInput || !isUnavailable(checkedInput)) return;

      var firstAvailable = inputs.find(function (input) {
        return input !== checkedInput && !isUnavailable(input);
      });
      if (!firstAvailable) return;

      firstAvailable.checked = true;
      fixedInput = firstAvailable;
    });

    return fixedInput;
  }

  var checkPending = false;
  function scheduleCheck() {
    if (checkPending) return;
    checkPending = true;
    setTimeout(function () {
      checkPending = false;
      document.querySelectorAll("variant-picker").forEach(function (picker) {
        var fixedInput = tryFixPicker(picker);
        if (fixedInput) {
          fixedInput.dispatchEvent(new Event("change", { bubbles: true }));
        }
      });
    }, 30);
  }

  var observeRoot = document.getElementById("main") || document.body;
  new MutationObserver(scheduleCheck).observe(observeRoot, { childList: true, subtree: true });
})();
